import { INVENTORIZATION_PHOTO_SLOT_IDS } from '@/shared/capture/inventorization-photo-slots';

export const INVENTORIZE_REVIEW_FIELD_ID_PREFIXES = {
  photos: 'photos.',
  registers: 'registers.',
  technicalDetail: 'technical.detail.',
  technicalOutputs: 'technical.outputs.',
} as const;

export const INVENTORIZE_POSITION_REVIEW_FIELD_IDS = {
  floorplan: 'position.floorplan',
  gps: 'position.gps',
} as const;

export const INVENTORIZE_CONTEXT_REVIEW_FIELD_IDS = {
  buildingName: 'context.buildingName',
  floorName: 'context.floorName',
  locationName: 'context.locationName',
  ownerName: 'context.ownerName',
  tenantName: 'context.tenantName',
} as const;

export const INVENTORIZE_IDENTITY_REVIEW_FIELD_IDS = {
  apiKey: 'identity.apiKey',
  calibrated: 'identity.calibrated',
  commodity: 'identity.commodity',
  manufactured: 'identity.manufactured',
  manufacturer: 'identity.manufacturer',
  meterModel: 'identity.meterModel',
  newMeter: 'identity.newMeter',
  placement: 'identity.placement',
  serial: 'identity.serial',
} as const;

export const INVENTORIZE_TECHNICAL_REVIEW_FIELD_IDS = {
  accessibility: 'technical.accessibility',
  monitoredEntity: 'technical.monitoredEntity',
  mountTechnology: 'technical.mountTechnology',
  mountType: 'technical.mountType',
  note: 'technical.note',
  remoteReadingOwner: 'technical.remoteReadingOwner',
  remoteReadingSerial: 'technical.remoteReadingSerial',
} as const;

export const INVENTORIZE_TECHNICAL_DETAIL_REVIEW_FIELD_IDS = {
  lorawanRssi: createInventorizeTechnicalDetailReviewFieldId('lorawanRssi'),
  nbiotRssi: createInventorizeTechnicalDetailReviewFieldId('nbiotRssi'),
} as const;

export const INVENTORIZE_PHOTO_REVIEW_FIELD_IDS = {
  context: `${INVENTORIZE_REVIEW_FIELD_ID_PREFIXES.photos}${INVENTORIZATION_PHOTO_SLOT_IDS.context}`,
  detail: `${INVENTORIZE_REVIEW_FIELD_ID_PREFIXES.photos}${INVENTORIZATION_PHOTO_SLOT_IDS.detail}`,
  module: `${INVENTORIZE_REVIEW_FIELD_ID_PREFIXES.photos}${INVENTORIZATION_PHOTO_SLOT_IDS.module}`,
  registerMain: `${INVENTORIZE_REVIEW_FIELD_ID_PREFIXES.photos}${INVENTORIZATION_PHOTO_SLOT_IDS.registerMain}`,
} as const;

export type InventorizePhotoReviewFieldId =
  (typeof INVENTORIZE_PHOTO_REVIEW_FIELD_IDS)[keyof typeof INVENTORIZE_PHOTO_REVIEW_FIELD_IDS];

export const INVENTORIZE_PHOTO_REVIEW_FIELD_SLOT_IDS = {
  [INVENTORIZE_PHOTO_REVIEW_FIELD_IDS.context]: INVENTORIZATION_PHOTO_SLOT_IDS.context,
  [INVENTORIZE_PHOTO_REVIEW_FIELD_IDS.detail]: INVENTORIZATION_PHOTO_SLOT_IDS.detail,
  [INVENTORIZE_PHOTO_REVIEW_FIELD_IDS.module]: INVENTORIZATION_PHOTO_SLOT_IDS.module,
  [INVENTORIZE_PHOTO_REVIEW_FIELD_IDS.registerMain]: INVENTORIZATION_PHOTO_SLOT_IDS.registerMain,
} as const satisfies Record<InventorizePhotoReviewFieldId, string>;

export const INVENTORIZE_REGISTER_REVIEW_FIELD_KEYS = {
  counter: 'counter',
  label: 'label',
  obis: 'obis',
  tariff: 'tariff',
  unit: 'unit',
} as const;

export type InventorizeRegisterReviewFieldKey =
  (typeof INVENTORIZE_REGISTER_REVIEW_FIELD_KEYS)[keyof typeof INVENTORIZE_REGISTER_REVIEW_FIELD_KEYS];

export interface InventorizeRegisterReviewFieldId {
  field: InventorizeRegisterReviewFieldKey;
  registerId: string;
}

const inventorizeRegisterReviewFieldKeys = new Set<string>(Object.values(INVENTORIZE_REGISTER_REVIEW_FIELD_KEYS));

export function createInventorizeOutputReviewFieldId(index: number) {
  return `${INVENTORIZE_REVIEW_FIELD_ID_PREFIXES.technicalOutputs}${index}`;
}

export function createInventorizeRegisterReviewFieldId(registerId: string, field: InventorizeRegisterReviewFieldKey) {
  return `${INVENTORIZE_REVIEW_FIELD_ID_PREFIXES.registers}${registerId}.${field}`;
}

export function readInventorizeRegisterReviewFieldId(fieldId: string): InventorizeRegisterReviewFieldId | null {
  if (!fieldId.startsWith(INVENTORIZE_REVIEW_FIELD_ID_PREFIXES.registers)) {
    return null;
  }

  const normalized = fieldId.slice(INVENTORIZE_REVIEW_FIELD_ID_PREFIXES.registers.length);
  const [registerId, field, ...rest] = normalized.split('.');

  if (!(registerId && field && rest.length === 0 && inventorizeRegisterReviewFieldKeys.has(field))) {
    return null;
  }

  return {
    field: field as InventorizeRegisterReviewFieldKey,
    registerId,
  };
}

export function createInventorizeTechnicalDetailReviewFieldId(key: string) {
  return `${INVENTORIZE_REVIEW_FIELD_ID_PREFIXES.technicalDetail}${key}`;
}

export function readInventorizeTechnicalDetailReviewFieldKey(fieldId: string) {
  return fieldId.startsWith(INVENTORIZE_REVIEW_FIELD_ID_PREFIXES.technicalDetail)
    ? fieldId.slice(INVENTORIZE_REVIEW_FIELD_ID_PREFIXES.technicalDetail.length)
    : null;
}
